'use client'

import { ROUTES } from '@/core/constants/routes'
import { Button } from '@/core/ui/atoms/button'
import { FormDateInput } from '@/core/ui/forms/date-input'
import { FormInput } from '@/core/ui/forms/input'
import { Link, usePathname } from '@/i18n/navigation'
import { CornerUpLeft } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { FormProvider } from 'react-hook-form'
import { useCreateAccount } from './use-create-account'

type Props = {
  onSuccess: () => void
}

export const CreateAccountForm: React.FC<Props> = props => {
  const t = useTranslations()
  const { form, handleSubmit, isPending } = useCreateAccount(props)
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const getSigninLink = () => {
    const params = new URLSearchParams(searchParams)
    params.set('auth', 'login')
    return `${pathname}?${params.toString()}`
  }

  return (
    <div className='px-4 py-4 sm:px-10 sm:py-5'>
      <div className='mb-3 flex items-center gap-3 sm:gap-6'>
        <Link href={getSigninLink()}>
          <CornerUpLeft className='h-5 w-5 cursor-pointer text-gray-700' />
        </Link>
        <h1 className='text-mountain-bluebird text-base font-semibold sm:text-2xl'>
          {t('createAccount.title')}
        </h1>
      </div>

      <p className='text-mountain-bluebird mb-4 text-sm'>
        {t('createAccount.subtitle')}
      </p>

      <FormProvider {...form}>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <div className='flex items-start gap-2'>
            <FormInput
              name='username'
              label={t('forms.usernameLabel')}
              placeholder={t('forms.usernamePH')}
              control={form.control}
              labelClassName='text-mountain-bluebird'
              className='rounded-sm'
            />
            <FormInput
              name='name'
              label={t('forms.nameLabel')}
              placeholder={t('forms.namePH')}
              control={form.control}
              labelClassName='text-mountain-bluebird'
              className='rounded-sm'
            />
          </div>

          <FormInput
            name='email'
            label={t('forms.emailLabel')}
            placeholder={t('forms.emailPH')}
            control={form.control}
          />

          <div className='flex items-start gap-2'>
            <FormInput
              name='password'
              label={t('forms.passwordLabel')}
              placeholder={t('forms.passwordPH')}
              type='password'
              control={form.control}
            />
            <FormInput
              name='password_confirmation'
              label={t('forms.passwordConfirmationLabel')}
              placeholder={t('forms.passwordConfirmationPH')}
              type='password'
              control={form.control}
            />
          </div>

          <FormDateInput
            name='birthdate'
            label={t('forms.birthdateLabel')}
            placeholder={t('forms.birthdatePH')}
            control={form.control}
            maxDate={'today'}
          />

          <Button
            loading={isPending}
            className='mt-1 h-12 w-full cursor-pointer'
          >
            {t('actions.continue')}
          </Button>

          <p className='text-mountain-bluebird mt-2 text-sm leading-snug'>
            {t('acceptTerms.accept')}{' '}
            <Link href={ROUTES.terms} className='text-creamed-cron underline'>
              {t('terms.title')}
            </Link>{' '}
            {t('conjunctions.and')}{' '}
            <Link href={ROUTES.privacy} className='text-creamed-cron underline'>
              {t('privacy.title')}
            </Link>
            .
          </p>

          <p className='mt-2 text-center text-base text-gray-700'>
            {t('createAccount.alreadyCustomer')}{' '}
            <Link
              href={getSigninLink()}
              className='text-creamed-cron font-medium underline'
            >
              {t('links.signin')}
            </Link>
          </p>
        </form>
      </FormProvider>
    </div>
  )
}
